import cc from 'classcat';
import { useRef, useCallback, useEffect } from 'react';
import { v4 } from 'uuid';
import { Sheet } from '@i-ark/components';
import { getPlatform, itemSupportsRotation } from '@i-ark/utils';
import { typesetKatex } from '@utils/common';
import { useComponentEntry } from '@utils/component-framework';
import { useFullscreenState, CSSRefTransition } from '@utils/react';
import '@i-ark/models';
import { useCreateCloneEntryMutation } from '../../state/mutations/entries/clone.create.js';
import { useMoveCloneEntryMutation } from '../../state/mutations/entries/clone.move.js';
import { useMoveItemEntryMutation } from '../../state/mutations/entries/item.move.js';
import '@tanstack/react-query';
import { selectPageEntryById } from '../../state/selectors/entry.page-by-id.js';
import { iArkStore } from '../../state/store.js';
import { useUserOwnsEntry } from '../hook.user-owns-entry.js';
import { sheetAspectRatio, aspectContainer, zoomContainer, drawToolCls, sheetCls } from './_sheet.style.js';
import { DrawLineTool } from './atom.draw-line-tool/_tool.js';
import { After } from './components/After.js';
import { Hotspot } from './components/Hotspot.js';
import { Input } from './components/Input.js';
import { TableCell } from './components/TableCell.js';
import { Text } from './components/Text.js';
import { Line } from './components/Line.js';
import { containerCls } from './components/RichTextInput.style.js';
import { enrichPageItems } from './utils/enrichPageItems.js';
import { handleDrag } from './utils/handleDrag.js';
import { itemSupportsDrag } from './utils/itemSupportsDrag.js';
import { jsx, jsxs } from 'react/jsx-runtime';

const CLONE_TRANSITION_DURATION = 100;
const HOVER_TRANSITION_DURATION = 150;
const DRAW_TOOL_TRANSITION_DURATION = 100;
function SheetWrapper({
  page,
  defaultInputColor,
  defaultTextColor,
  context,
  isPrinting,
  ref: externalRef
}) {
  const internalRef = useRef(null);
  const sheetRef = externalRef ?? internalRef;
  const {
    fullscreenEnabled
  } = useFullscreenState();
  const entryKey = iArkStore.useSelector(state => state.entryKey);
  const dispatch = iArkStore.useDispatch();
  const moveItemMutation = useMoveItemEntryMutation(entryKey);
  const cloneItemMutation = useCreateCloneEntryMutation(entryKey);
  const moveCloneMutation = useMoveCloneEntryMutation(entryKey);
  const selectedItemId = iArkStore.useSelector(state => state.selectedItemId);
  const lineToolEnabled = iArkStore.useSelector(state => state.lineToolEnabled);
  const zoom = iArkStore.useSelector(state => state.zoom);
  const userOwnsEntry = useUserOwnsEntry(entryKey);
  const pageEntry = useComponentEntry(entryKey, {
    select: selectPageEntryById(page.id)
  });
  const defaultHotspotColor = "rgba(0, 0, 0, 0)";
  const handleDragStart = useCallback((evt, item) => {
    if (!userOwnsEntry || !itemSupportsDrag(item) || !item.settings.movable && !item.settings.cloneable) {
      return;
    }
    if ("button" in evt && evt.button !== 0) {
      // don't begin dragging on anything but a main button (left) click
      return;
    }
    const sheetNode = sheetRef.current;
    const itemNode = evt.currentTarget.closest("[data-drag-target]");
    if (sheetNode == null || itemNode === null) {
      return;
    }
    let isDragging = false;

    // determine if the item is a clone by checking to see that the id
    // doesn't exist in the pure sheet configuration
    const isItemCloned = page.items.find(it => it.id === item.id) === undefined;

    // assume that we're moving an item if the movable setting is enabled,
    // or if we're already working with a clone of the item
    const isCloning = !item.settings.movable && !isItemCloned;
    let draggedNode = undefined;
    const createPreview = () => {
      // create a clone of the item node that we can use while handling
      // drag'n'drop, so we don't need to rely on Reacts built-in rendering
      // (this will greatly optimize performance)
      draggedNode = itemNode.cloneNode(true);
      draggedNode.classList.add("draggedElement");
      draggedNode.classList.add("dragPreview");
      itemNode.classList.add("draggedElement");
      if (isCloning) {
        const richtextNode = draggedNode.querySelector(".fr-view");
        const richtextInputContainer = draggedNode.querySelector(`.${containerCls}`);
        if (richtextNode) {
          richtextInputContainer?.classList.add("empty");
          richtextNode.innerHTML = "";
        }
      }
      setTimeout(() => {
        draggedNode?.classList.add("dragPreview--entered");
      });

      // force cloned node to be rendered on top of all other nested
      // contents
      draggedNode.style.zIndex = "100";

      // if a canvas was part of the cloned node, then copy it's image data
      // onto the temporary drag clone
      const itemCanvas = itemNode.querySelector("canvas");
      const itemCanvasCtx = itemCanvas?.getContext("2d");
      const draggedCanvas = draggedNode.querySelector("canvas");
      const draggedCanvasCtx = draggedCanvas?.getContext("2d");
      if (itemCanvas !== null && itemCanvasCtx != null && draggedCanvas !== null && draggedCanvasCtx != null) {
        draggedCanvas.width = itemCanvas.width;
        draggedCanvas.height = itemCanvas.height;
        const imageData = itemCanvasCtx?.getImageData(0, 0, itemCanvas.width, itemCanvas.height);
        draggedCanvasCtx.putImageData(imageData, 0, 0);
      }
      if (isCloning) {
        // if we're creating a clone, then actually clone the original node
        // and then reduce it's opacity while dragging
        draggedNode.style.opacity = "0.5";
      } else {
        // if we're moving a node without cloning, then hide the native
        // React node while handling Drag'n'Drop through our custom
        // integration so we avoid multiple nodes being displayed at once
        itemNode.style.opacity = "0";
      }
      sheetNode.appendChild(draggedNode);

      // indicate that the node is being dragged
      document.body.classList.add("isDragging");
      return draggedNode;
    };

    // follow cursor movement when user moves the item around
    const onDrag = (offset, enableTouchDrag) => {
      if (!isDragging) {
        const draggedNode = createPreview();

        // flash an overlay on top of the draggable item, indicating that it
        // can now be dragged
        if (enableTouchDrag === true) {
          const touchFlashOverlay = document.createElement("div");
          touchFlashOverlay.style.position = "absolute";
          touchFlashOverlay.style.top = "-10px";
          touchFlashOverlay.style.bottom = "-10px";
          touchFlashOverlay.style.left = "-10px";
          touchFlashOverlay.style.right = "-10px";
          touchFlashOverlay.style.zIndex = "100";
          touchFlashOverlay.style.backgroundColor = "#000";
          touchFlashOverlay.style.opacity = "0";
          touchFlashOverlay.style.transition = "opacity 0.2s linear";
          draggedNode.appendChild(touchFlashOverlay);
          setTimeout(() => {
            touchFlashOverlay.style.opacity = "0.1";
            setTimeout(() => {
              touchFlashOverlay.style.opacity = "0";
              setTimeout(() => {
                draggedNode.removeChild(touchFlashOverlay);
              }, 200);
            }, 200);
          });
        }
        isDragging = true;
      }
      if (!draggedNode) {
        return;
      }

      // calculate applied rotation to ensure that drag preview
      // retains the same rotation as the original node
      const rotation = itemSupportsRotation(item) ? `rotate(${item.angleInRadian.toString()}rad)` : "";
      const offsetX = Math.min(1 - item.position.x - item.size.width, Math.max(-item.position.x, offset.x));
      const offsetY = Math.min(1 - item.position.y - item.size.height, Math.max(-item.position.y, offset.y));
      draggedNode.style.transform = `
        translate(${(sheetNode.clientWidth * offsetX).toString()}px, ${(sheetNode.clientHeight * offsetY).toString()}px) ${rotation}
      `;
    };
    const onDrop = offset => {
      if (!draggedNode) {
        return;
      }
      if (!isDragging) {
        // if user never began to drag the item, then simply remove the
        // clone from the DOM instantly to avoid having unwanted nodes
        // mounted within React
        itemNode.style.opacity = "";
        sheetNode.removeChild(draggedNode);
        itemNode.classList.remove("draggedElement");
        return;
      }
      if (offset !== undefined) {
        const posX = Math.min(1 - item.size.width, Math.max(0, item.position.x + offset.x));
        const posY = Math.min(1 - item.size.height, Math.max(0, item.position.y + offset.y));
        if (item.settings.movable) {
          moveItemMutation({
            pageId: page.id,
            itemId: item.id,
            position: {
              x: posX,
              y: posY
            }
          });
        } else if (isItemCloned) {
          moveCloneMutation({
            pageId: page.id,
            cloneId: item.id,
            position: {
              x: posX,
              y: posY
            }
          });
        } else {
          const cloneId = `clone:${v4()}`;
          cloneItemMutation({
            pageId: page.id,
            itemId: item.id,
            cloneId,
            position: {
              x: posX,
              y: posY
            }
          });
          dispatch(iArkStore.actions.selectItem(cloneId));
          setTimeout(() => {
            sheetNode.querySelector(`[data-id="${cloneId}"] button`)?.focus({
              preventScroll: true
            });
          });
        }
      }
      document.body.classList.remove("isDragging");

      // reset temporary changing of zIndex on the original item which was
      // applied to creat a drag'n'drop state that wasn't managed by React
      if (isCloning) {
        // if we need to fade the clone into view before removing it, then
        // apply a full fade-in transition, followed by a fade-out
        // transition followed by completely removing it from the DOM
        draggedNode.style.transition = `opacity ${CLONE_TRANSITION_DURATION.toString()}ms linear`;
        setTimeout(() => {
          if (draggedNode) {
            draggedNode.style.opacity = "0";
          }
          setTimeout(() => {
            draggedNode?.remove();
            setTimeout(() => {
              itemNode.classList.remove("draggedElement");
            }, HOVER_TRANSITION_DURATION);
          }, CLONE_TRANSITION_DURATION);
        });
      } else {
        requestAnimationFrame(() => {
          // Defer removal until next tick to avoid jumps due to state
          // syncing
          setTimeout(() => {
            // show the native React node again once the drag'n'drop has
            // ended
            itemNode.style.opacity = "";

            // ... and remove our clone from the DOM completely
            draggedNode?.remove();
          });
          setTimeout(() => {
            itemNode.classList.remove("draggedElement");
          }, HOVER_TRANSITION_DURATION);
        });
      }
    };
    const onCancel = () => {
      onDrop();
    };
    handleDrag({
      evt,
      sheet: sheetRef.current,
      onDrag,
      onDrop,
      onCancel
    });
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [userOwnsEntry, page, entryKey]);
  const handleSelect = useCallback((evt, item) => {
    dispatch(iArkStore.actions.selectItem(item.id));
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [userOwnsEntry, page, entryKey]);
  useEffect(() => {
    typesetKatex(sheetRef);
  }, [sheetRef]);
  return /*#__PURE__*/jsx("div", {
    "data-orientation": page.aspectRatio <= 1 ? "landscape" : "portrait",
    className: cc([sheetCls, (fullscreenEnabled || context === "lightbox") && "dynamicWidth", getPlatform().isFirefox && "firefox"]),
    style: sheetAspectRatio.__override(page.aspectRatio),
    children: /*#__PURE__*/jsx("div", {
      className: aspectContainer,
      children: /*#__PURE__*/jsxs("div", {
        className: zoomContainer,
        children: [/*#__PURE__*/jsx(Sheet, {
          isPrinting: isPrinting,
          ref: sheetRef,
          zoom: zoom,
          page: {
            ...page,
            items: enrichPageItems(page, pageEntry.data)
          },
          cloneIds: pageEntry.data?.clones.map(it => it.cloneId) ?? [],
          selectedItemId: selectedItemId,
          defaultTextColor: defaultTextColor,
          defaultInputColor: defaultInputColor,
          defaultHotspotColor: defaultHotspotColor,
          After: After,
          Input: Input,
          Text: Text,
          TableCell: TableCell,
          Hotspot: Hotspot,
          Line: Line,
          onSelect: handleSelect,
          onDragStart: handleDragStart
        }), /*#__PURE__*/jsx(CSSRefTransition, {
          in: lineToolEnabled,
          classNames: drawToolCls,
          timeout: DRAW_TOOL_TRANSITION_DURATION,
          mountOnEnter: true,
          unmountOnExit: true,
          children: ref => /*#__PURE__*/jsx(DrawLineTool, {
            ref: ref,
            pageId: page.id
          })
        })]
      })
    })
  });
}

export { SheetWrapper };
//# sourceMappingURL=_sheet.js.map
